/**
 * Positions from map.svg `#lands` groups, scaled SVG (2222×1263) → world (9960×5662).
 * Scale: 9960/2222 ≈ 5662/1263.
 *
 * Order matches SVG paint order (later = on top).
 */
export const MAP_WIDTH = 9960;
export const MAP_HEIGHT = 5662;

/**
 * World display scale relative to design size (1 = 100%).
 * Fit-to-viewport × 2.73375, then 50% farther, then another 30%, then +20% twice.
 */
export const MAP_SCALE =
  Math.min(1080 / MAP_WIDTH, 1920 / MAP_HEIGHT) * 2.73375 * 0.5 * 0.7 * 1.2 * 1.2;

/**
 * CSS bitmap size vs world box. Matches max map zoom so grayscale/filter
 * buffers stay near on-screen size instead of thousands of world pixels.
 */
export const MAP_PAINT_SCALE = MAP_SCALE * 1.44;

export function mapPaintBoxStyle(
  left: number,
  top: number,
  width: number,
  height: number,
): {
  left: number;
  top: number;
  width: number;
  height: number;
  transform: string;
  transformOrigin: 'top left';
} {
  return {
    left,
    top,
    width: width * MAP_PAINT_SCALE,
    height: height * MAP_PAINT_SCALE,
    transform: `scale(${1 / MAP_PAINT_SCALE})`,
    transformOrigin: 'top left',
  };
}

/**
 * Counter-scale for map popups so CTAs stay tappable after fit-zoom.
 * Restores roughly the on-screen size from the previous MAP_SCALE 0.75,
 * then +30% for readability, then 50% smaller, then another 30%, then +20%.
 */
export const MAP_POPUP_COUNTER_SCALE = (0.75 * 1.3 * 0.5 * 0.7 * 1.2) / MAP_SCALE;

/** Arrow pan as a share of the CSS view width (legacy 400px on the 1080 design). */
export const MAP_PAN_STEP_VIEW_RATIO = 400 / 1080;

export function mapPanStep(viewWidth: number): number {
  return viewWidth * MAP_PAN_STEP_VIEW_RATIO;
}

export type LandId =
  | 'sol'
  | 'mu'
  | 'serj'
  | 'mona'
  | 'duck'
  | 'yetti'
  | 'svami'
  | 'roza'
  | 'vulcano'
  | 'factor'
  | 'drakochi';

export type LandPlacement = {
  id: LandId;
  left: number;
  top: number;
  width: number;
  height: number;
};

export const LAND_PLACEMENTS: readonly LandPlacement[] = [
  {
    id: 'sol',
    left: -334.168,
    top: -282.222,
    width: 5872.815,
    height: 2084.943,
  },
  {
    id: 'mu',
    left: 4461.681,
    top: -726.803,
    width: 6085.52,
    height: 2838.285,
  },
  {
    id: 'serj',
    left: -335.518,
    top: 395.664,
    width: 4186.573,
    height: 2575.887,
  },
  {
    id: 'mona',
    left: -490.864,
    top: 2257.242,
    width: 3985.17,
    height: 2305.353,
  },
  {
    id: 'duck',
    left: -227.738,
    top: 4046.671,
    width: 4576.17,
    height: 1999.206,
  },
  {
    id: 'yetti',
    left: 2811.167,
    top: 704.612,
    width: 5735.158,
    height: 3035.002,
  },
  {
    id: 'svami',
    left: 2331.653,
    top: 2675.804,
    width: 5389.857,
    height: 2407.13,
  },
  {
    id: 'roza',
    left: 6447.15,
    top: 433.602,
    width: 3950.695,
    height: 3031.448,
  },
  {
    id: 'vulcano',
    left: 3329.531,
    top: 2840.127,
    width: 6659.963,
    height: 2051.787,
  },
  {
    id: 'factor',
    left: 2557.026,
    top: 4011.417,
    width: 4927.273,
    height: 1828.826,
  },
  {
    id: 'drakochi',
    left: 5891.237,
    top: 3194.103,
    width: 4437.22,
    height: 2772.112,
  },
] as const;
